/* eslint-disable indent */
// import { uniqBy } from 'lodash';
import { TDamageSchema } from '@/schemas/damage/damage.schema';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { TVehiculeCheckResultSchema } from '@/schemas/vehicule-check-result/vehicule-check-result.schema';
import { getImageBlobFormData } from '@/utils/image-blob-form-data.utils';
import { getDateInISOFormat } from '@/utils/date.utils';
import {
  ResultModalStep,
  ResultPhotoTab,
} from '@/type/ui/tab/vehiculeCheckResultTab';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { TFunction } from 'i18next';
import { TVehiculeCheckResultCheckPointSchema } from '@/schemas/vehicule-check-result/vehicule-check-check-point.schema';
import { TResultObjectSchema } from '@/schemas/vehicule-check-result/result-object.schema';
import { TResultImageSchema } from '@/schemas/vehicule-check-result/result-image.schema';

interface ResultModalHeadingProps {
  t?: TFunction<undefined>;
  step?: string;
  activeTab: ResultPhotoTab;
  selectedResultObject?: TResultObjectSchema;
  isEdit?: boolean;
}

interface UploadFileToFolderProps {
  fileUrl: string;
  mutate: (formData: FormData) => void;
}

interface GetImageToAddProps {
  fileName?: string;
  coordinatesModerated?: TResultImageCoordinatesSchema;
}

interface GetImageToUpdateProps {
  image?: TResultImageSchema;
  fileName?: string;
  coordinatesModerated?: TResultImageCoordinatesSchema;
  deactivateSingleImage?: boolean;
  removeCoordinates?: boolean;
}

interface ManageResultImageProps {
  activeImageTab: ResultPhotoTab;
  vehiculeCheckResult?: TVehiculeCheckResultSchema;
  selectedEquipment?: TEquipmentSchema;
  selectedDamage?: TDamageSchema;
  fileName?: string;
  checkPointId?: string;
  addImage?: boolean;
  selectedResultObjectId?: string;
  currentImageIndex?: number;
  coordinatesModerated?: TResultImageCoordinatesSchema;
  removeCoordinates?: boolean;
  activateModeration?: boolean;
  canModerate: boolean;
  moderationStatus: string;
  deactivateSingleImage?: boolean;
  deactivateAllImages?: boolean;
  mutate: (vehiculeCheckResult: TVehiculeCheckResultSchema) => void;
}

const getModalTitle = ({
  t,
  activeTab,
  selectedResultObject,
  isEdit,
}: ResultModalHeadingProps) => {
  let title = '';

  switch (activeTab) {
    case ResultPhotoTab.EQUIPMENTS:
      title = isEdit
        ? `${t('common:modifyPhotoOfTheEquipment')} ${
            selectedResultObject?.equipment?.name
              ? `<span className="mx-1">-</span>${selectedResultObject?.equipment?.name}`
              : ''
          }`
        : `${t('common:addEquipmentPhoto')} ${
            selectedResultObject?.equipment?.name
              ? `<span className="mx-1">-</span>${selectedResultObject?.equipment?.name}`
              : ''
          }`;
      break;
    case ResultPhotoTab.DAMAGES:
      title = isEdit
        ? `${t('common:modifyPhotoOfTheDamage')} ${
            selectedResultObject?.damage?.name
              ? `<span className="mx-1">-</span>${
                  selectedResultObject?.damage?.name
                }`
              : ''
          }`
        : `${t('common:addDamagePhoto')} ${
            selectedResultObject?.damage?.name
              ? `<span className="mx-1">-</span>${
                  selectedResultObject?.damage?.name
                }`
              : ''
          }`;
      break;
  }

  return title;
};

export const getResultModalHeading = ({
  t,
  step,
  activeTab,
  selectedResultObject,
  isEdit,
}: ResultModalHeadingProps) => {
  let title = '';

  switch (step) {
    case ResultModalStep.SELECT_ENTITY:
      title = getModalTitle({ t, activeTab, selectedResultObject, isEdit });
      break;
    case ResultModalStep.CAPTURE_VIDEO:
    case ResultModalStep.CONFIRM_CAPTURE:
    case ResultModalStep.UPLOAD_IMAGE:
      title = getModalTitle({ t, activeTab, selectedResultObject, isEdit });
      break;
    case ResultModalStep.MODERATE:
      title = t('common:modifyModeration');
      break;
  }

  return { title };
};

export const uploadFileToFolder = async ({
  fileUrl,
  mutate,
}: UploadFileToFolderProps) => {
  const formData = await getImageBlobFormData(fileUrl);
  mutate(formData);
};

export const getRelatedObjectPayloads = (object) => {
  return {
    id: object?.id,
    name: object?.name,
    classId: object?.classId?.toString(),
    severity: object?.severity ? Number(object?.severity) : undefined,
    translatedEquipments: object?.translatedEquipments,
    translatedDamages: object?.translatedDamages,
  };
};

const getImageToAdd = ({
  fileName,
  coordinatesModerated,
}: GetImageToAddProps) => {
  return {
    id: crypto.randomUUID(),
    url: `images/${fileName}`,
    coordinatesModerated: coordinatesModerated
      ? { ...coordinatesModerated, moderated: true }
      : undefined,
    isDeleted: false,
    createdAt: getDateInISOFormat(new Date()),
    updatedAt: getDateInISOFormat(new Date()),
  };
};

export const addResultImage = ({
  activeImageTab,
  vehiculeCheckResult,
  selectedEquipment,
  selectedDamage,
  coordinatesModerated,
  fileName,
  checkPointId,
  canModerate,
  moderationStatus,
  mutate,
}: ManageResultImageProps) => {
  let newData = {};

  const updatedResults = vehiculeCheckResult?.results?.map((result) => {
    if (!result?.checkPoint || result.checkPoint.id !== checkPointId) {
      return result;
    }

    // const containsImageArray = result?.checkPoint?.[activeImageTab]?.some(
    //   (item) => Array.isArray(item?.images)
    // );

    // if (containsImageArray) {
    //   newData = {
    //     id: crypto.randomUUID(),
    //     createdAt: getDateInISOFormat(new Date()),
    //     updatedAt: getDateInISOFormat(new Date()),
    //     images: [{ ...getImageToAdd({ fileName, coordinatesModerated }) }],
    //     equipment: selectedEquipment,
    //     damage: selectedDamage
    //       ? {
    //           ...selectedDamage,
    //           severity: selectedDamage?.name?.split('_')?.pop(),
    //         }
    //       : undefined,
    //   };
    // } else {
    //   newData = {
    //     ...getImageToAdd({ fileName, coordinatesModerated }),
    //     image: `images/${fileName}`,
    //     url: undefined,
    //     name: selectedEquipment?.name,
    //     equipment: selectedEquipment,
    //   };
    // }

    newData = {
      id: crypto.randomUUID(),
      createdAt: getDateInISOFormat(new Date()),
      updatedAt: getDateInISOFormat(new Date()),
      images: [{ ...getImageToAdd({ fileName, coordinatesModerated }) }],
      equipment: selectedEquipment,
      damage: selectedDamage,
    };

    return {
      ...result,
      checkPoint: {
        ...result.checkPoint,
        [activeImageTab]: [
          ...(result.checkPoint[activeImageTab] ?? []),
          { ...newData },
        ],
      },
    };
  });

  mutate({
    id: vehiculeCheckResult?.id,
    results: updatedResults,
    canModerate,
    moderationStatus,
  });
};

const getImageToUpdate = ({
  image,
  fileName,
  coordinatesModerated,
  deactivateSingleImage,
  removeCoordinates,
}: GetImageToUpdateProps) => {
  return {
    url: fileName ? `images/${fileName}` : image?.url,
    coordinatesModerated: removeCoordinates
      ? undefined
      : coordinatesModerated
        ? { ...coordinatesModerated, moderated: true }
        : undefined,
    isDeleted: deactivateSingleImage ? true : false,
    deletedAt: deactivateSingleImage
      ? getDateInISOFormat(new Date())
      : undefined,
    updatedAt: getDateInISOFormat(new Date()),
  };
};

export const editResultImage = ({
  activeImageTab,
  vehiculeCheckResult,
  selectedEquipment,
  selectedDamage,
  fileName,
  checkPointId,
  addImage,
  selectedResultObjectId,
  currentImageIndex,
  coordinatesModerated,
  removeCoordinates,
  canModerate,
  moderationStatus,
  deactivateSingleImage,
  deactivateAllImages,
  mutate,
}: ManageResultImageProps) => {
  const updatedResults = vehiculeCheckResult?.results?.map((result) => {
    if (!result?.checkPoint || result.checkPoint.id !== checkPointId) {
      return result;
    }

    const getUpdatedData = (object: TResultObjectSchema) => {
      let updatedData = {};

      const activeImages = object?.images?.filter((image) => !image?.isDeleted);

      const imageIdToUpdate = activeImages?.[currentImageIndex]?.id;

      if (activeImages?.length > 0) {
        let updatedImages;

        if (addImage) {
          // Add new image to existing list
          updatedImages = [
            ...(activeImages || []),
            getImageToAdd({ fileName, coordinatesModerated }),
          ];
        } else {
          // Update existing images
          updatedImages = activeImages?.map((image) => {
            if (deactivateAllImages) {
              return {
                ...image,
                isDeleted: true,
                updatedAt: getDateInISOFormat(new Date()),
                deletedAt: getDateInISOFormat(new Date()),
              };
            } else if (image?.id === imageIdToUpdate) {
              return {
                ...image,
                ...getImageToUpdate({
                  image,
                  fileName,
                  coordinatesModerated,
                  deactivateSingleImage,
                  removeCoordinates,
                }),
              };
            }

            return image;
          });
        }

        updatedData = {
          ...object,
          images: updatedImages,
          equipment: selectedEquipment,
          damage: selectedDamage,
        };
      } else {
        updatedData = {
          ...object,
          ...getImageToUpdate({
            image: object,
            fileName,
            coordinatesModerated,
            deactivateSingleImage,
            removeCoordinates,
          }),
          // Temporarily set customImage to null when updating image
          ...(fileName && { customImage: null }),
          ...(fileName && { image: `images/${fileName}` }),
          url: undefined,
          name: selectedEquipment?.name,
          equipment: selectedEquipment,
        };
      }

      return updatedData;
    };

    return {
      ...result,
      checkPoint: {
        ...result.checkPoint,
        [activeImageTab]:
          result.checkPoint[activeImageTab]?.map((object) =>
            object?.id === selectedResultObjectId
              ? getUpdatedData(object)
              : object,
          ) ?? [],
      },
    };
  });

  mutate({
    id: vehiculeCheckResult?.id,
    results: updatedResults,
    canModerate,
    moderationStatus,
  });
};

export const extractListPhotos = (
  results: TVehiculeCheckResultCheckPointSchema[],
  property: 'equipments' | 'damages' | 'vehicules',
) => {
  if (!results?.length) {
    return [];
  }

  const propertyItems = results
    .map((item) => item?.checkPoint?.[property])
    .filter((item) => !!item)
    .flat();

  const containsImageArray = propertyItems?.some((item) =>
    Array.isArray(item?.images),
  );

  if (containsImageArray) {
    return propertyItems?.filter(
      (item) =>
        item?.images?.length > 0 &&
        item?.images?.some((image) => !image?.isDeleted),
    );
  } else if (property === 'vehicules') {
    // return uniqBy(propertyItems, 'name');

    const imageCheckPoints = results.filter(
      (item) =>
        item?.checkPoint?.imageCheckPoint &&
        item?.checkPoint?.isVideo === false,
    );

    return imageCheckPoints;
  } else {
    return [];
  }
};

export const getPreparedEquipment = (equipment: TEquipmentSchema) => {
  return {
    id: equipment?.id,
    name: equipment?.name,
    classId: equipment?.classId,
  };
};

export const getPreparedDamage = (damage: TDamageSchema) => {
  return {
    id: damage?.id,
    name: damage?.name,
    severity: damage?.severity,
    classId: damage?.classId,
  };
};

const PDF_REDIRECT_QUERY_KEYS = [
  'checkPointId',
  'resultObjectId',
  'toDamageList',
  'checkPointImageUrl',
];

export const removedUrlQueriesFromPdfRedirect = () => {
  // Remove preview-related query params from the URL using native API
  // and also sync React Router by dispatching a popstate event
  const url = new URL(window.location.href);
  PDF_REDIRECT_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));
  window.history.replaceState({}, '', url.toString());
  // Dispatch popstate so React Router picks up the URL change
  window.dispatchEvent(new PopStateEvent('popstate'));
};
